/**
 * AI Usage Analytics View
 *
 * Surfaces usage, cost and operational health of the internal AI chat backend.
 * (Internal codename "Ion" — not exposed in the UI; retained in table names.)
 *
 * Two distinct data sources, visually distinguished:
 *   - Computed (internal SQL on ion_llm_calls)     — default styling
 *   - AWS-sourced (CloudWatch + Cost Explorer)       — cyan "AWS" pill + tooltip
 *
 * POPULATION (DEC-4): every figure here — /summary's KPI cards and /by-user's
 * table alike — covers ALL Ion spend of this environment, eval runs and
 * background jobs included. That is what makes it comparable to the AWS totals
 * next to it (AWS bills the whole account; it does not know what "customer
 * traffic" is). The per-feature split, and the only eval-excluding view, is
 * AnalyticsAiSurfaceSpendView.
 *
 * AWS data is authoritative for billing but lags ~24h and has no per-user
 * attribution. A >5% divergence between computed and AWS totals (same
 * window, computed SERVER-SIDE in admin/ai_usage/router.py::get_aws_actuals
 * — also logged as a warning there, so it's alertable, not just eyeballed)
 * triggers a sanity warning banner here — but only when both sides have the
 * same scope. AWS spend is account-wide (aws_spend_scope: 'account'; staging
 * and production share one account), so a gap against this environment's
 * computed cost is shown as a neutral informational note instead.
 */
import * as echarts from 'echarts';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getAiUsageByUser } from '../../../services/adminService.js';
import { authenticatedFetch } from '../../../services/authService.js';
import { COLORS, chartTheme } from './shared/chartTheme.js';
import { formatNumber, formatUSD, getApiBase } from './shared/formatters.js';
import { Icons } from './shared/Icons.jsx';
import { KPICard } from './shared/KPICard.jsx';

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

// `formatUSD` lives in ./shared/formatters.js so this view and the sibling
// spend-by-feature view round dollars identically.

function fmtRelTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const deltaMin = (Date.now() - d.getTime()) / 60000;
  if (deltaMin < 1) return 'gerade eben';
  if (deltaMin < 60) return `${Math.round(deltaMin)} min`;
  if (deltaMin < 1440) return `${Math.round(deltaMin / 60)} h`;
  return `${Math.round(deltaMin / 1440)} d`;
}

// Cache-hit-rate color coding (AI statistics dashboard, 2026-07) — thresholds
// per the existing `_cache_hit_rate_pct` docstring in router.py: a healthy
// multi-turn workload sits 70-95%; below 40% means the cache breakpoint
// placement likely isn't paying off at all. Shared by both the token- and
// call-weighted cards so the two read consistently at a glance.
function cacheRateColor(pct) {
  if (pct >= 70) return 'green';
  if (pct >= 40) return 'orange'; // "amber" — no distinct amber KPI-card variant exists
  return 'red';
}

// Small inline read-vs-write bar for the "Cache Read vs Write" KPI card.
// Read (cached_input_tokens) and write (cache_write_tokens) are different
// units of the SAME cache mechanism (read = served from cache, write =
// newly cached this call) — shown as a proportion of their combined volume,
// not a percentage of total tokens.
function CacheReadWriteBar({ readTokens, writeTokens }) {
  const total = Math.max(1, readTokens + writeTokens);
  const readPct = (readTokens / total) * 100;
  return (
    <div style={{ width: '100%' }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
        {formatNumber(readTokens)} / {formatNumber(writeTokens)}
      </div>
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: 6,
          borderRadius: 3,
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.08)',
        }}
        title={`Read: ${formatNumber(readTokens)} tokens · Write: ${formatNumber(writeTokens)} tokens`}
      >
        <div style={{ width: `${readPct}%`, background: COLORS.cyan }} />
        <div style={{ width: `${100 - readPct}%`, background: COLORS.orange }} />
      </div>
    </div>
  );
}

function AwsBadge() {
  return (
    <span
      className="aws-badge"
      title="Von AWS aggregiert (CloudWatch / Cost Explorer). Verzögerung ~24h bei Cost Explorer."
      style={{
        display: 'inline-block',
        marginLeft: 6,
        padding: '2px 6px',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.3,
        color: '#fff',
        background: COLORS.cyan,
        borderRadius: 3,
        verticalAlign: 'middle',
      }}
    >
      AWS
    </span>
  );
}

// The AWS tiles cover aws-actuals' own trailing window (range_days), NOT the
// calendar month — labelling them "MTD" next to the real MTD tiles above
// suggested the two were comparable. No range_days (fetch failed, nothing
// loaded yet) -> no window suffix rather than a wrong one.
function awsWindowLabel(rangeDays) {
  const n = Number(rangeDays);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n === 1 ? ' (letzter Tag)' : ` (letzte ${n} Tage)`;
}

// The Cost Explorer spend and the computed-vs-AWS comparison cover
// [window_start, window_end): ISO dates, UTC days, end EXCLUSIVE, ending at
// yesterday 00:00 UTC because Cost Explorer lags ~24h (issue #12). Rendered
// with the last INCLUDED day, e.g. "03.09.2026 – 02.10.2026". Null when the
// payload has no window (cached before the field existed) so callers fall
// back to the range_days label.
function isoDayParts(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

function formatUtcDay(ms) {
  const d = new Date(ms);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getUTCFullYear()}`;
}

function awsComparisonWindow(windowStart, windowEnd) {
  const s = isoDayParts(windowStart);
  const e = isoDayParts(windowEnd);
  if (!s || !e) return null;
  const startMs = Date.UTC(s[0], s[1] - 1, s[2]);
  const lastMs = Date.UTC(e[0], e[1] - 1, e[2]) - 86400000;
  if (lastMs < startMs) return null;
  const first = formatUtcDay(startMs);
  const last = formatUtcDay(lastMs);
  return first === last ? first : `${first} – ${last}`;
}

// aws-actuals says when an AWS read failed (cloudwatch.status,
// cost_explorer.status); the tiles then show this warning and "—" instead of
// a 0 that reads like "Bedrock was idle". Payloads without a status (cached
// before the field existed) render as before.
const CW_ACCESS_DENIED_FALLBACK = 'CloudWatch access denied: IAM needs cloudwatch:GetMetricData';
const CE_UNAVAILABLE_MESSAGE =
  'Cost Explorer unavailable: IAM needs ce:GetCostAndUsage (or Cost Explorer is not enabled)';

function awsReadProblem(awsActuals) {
  const cw = awsActuals?.cloudwatch;
  let cloudwatch = null;
  if (cw?.status === 'access_denied') cloudwatch = cw.error || CW_ACCESS_DENIED_FALLBACK;
  else if (cw?.status === 'error') cloudwatch = cw.error || 'CloudWatch read failed';
  const costExplorer =
    awsActuals?.cost_explorer?.status === 'unavailable' ? CE_UNAVAILABLE_MESSAGE : null;
  return { cloudwatch, costExplorer };
}

function AwsTileWarning({ message }) {
  return (
    <span className="aws-tile-warning" role="alert" style={{ color: COLORS.red }}>
      ⚠️ {message}
    </span>
  );
}

// --------------------------------------------------------------------------
// Summary cards
// --------------------------------------------------------------------------

// Exported for the label/divergence render test; the view itself is the only
// production consumer.
export function SummarySection({ summary, awsActuals, loading }) {
  if (loading || !summary) {
    return <div className="analytics-loading"><div className="spinner" /></div>;
  }

  // Divergence is computed SERVER-SIDE now (admin/ai_usage/router.py::get_aws_actuals),
  // comparing computed-vs-AWS spend over the SAME window (window_start ..
  // window_end, range_days ending yesterday 00:00 UTC; older payloads carry
  // only range_days) — the old client-side version mismatched windows (calendar
  // MTD vs an arbitrary range_days) and was purely eyeballed, not alertable.
  const computedTotal = parseFloat(awsActuals?.computed_cost_usd ?? '0');
  const awsTotal = parseFloat(awsActuals?.cost_explorer?.total_cost_usd || '0');
  const delta = awsActuals?.divergence_pct ?? 0;
  // Cost Explorer bills the whole AWS account (staging + production share
  // one), computed_cost_usd counts only this environment — a gap between
  // the two is expected, not a pricing error. The server says so with
  // aws_spend_scope + divergence_informational; responses cached before those
  // fields existed lack both and keep the old warning behaviour.
  const accountWideSpend = awsActuals?.aws_spend_scope === 'account';
  const divergenceWarning = !!awsActuals?.divergence_warning && !accountWideSpend;
  const divergenceInformational =
    !divergenceWarning &&
    (!!awsActuals?.divergence_informational || (accountWideSpend && !!awsActuals?.divergence_warning));
  const awsWindow = awsWindowLabel(awsActuals?.range_days);
  // CloudWatch tiles keep the trailing range_days window; the spend tile and
  // the divergence notes show the Cost Explorer window both sides share.
  const ceWindow = awsComparisonWindow(awsActuals?.window_start, awsActuals?.window_end);
  const spendWindow = ceWindow ? ` (${ceWindow})` : awsWindow;
  const comparisonPeriod = ceWindow
    ? `${ceWindow} UTC, bis gestern 00:00`
    : `${awsActuals?.range_days ?? '?'} Tage`;
  const awsProblem = awsReadProblem(awsActuals);
  // A metric that could not be read arrives as null; `|| 0` would turn it
  // back into the silent zero this guards against, so null renders as "—".
  const cwValue = (n) => (awsProblem.cloudwatch && n == null ? '—' : formatNumber(n || 0));

  return (
    <div className="ai-usage-summary">
      <h3 className="section-title">Zusammenfassung</h3>
      {/* DEC-3 (ai-statistics-dashboard-plan.md §9): this router always reads
          the LOCAL session (get_db -> get_async_session_local()), never the
          prod-redirecting analytics session — even though this view is
          nested under the "Analytics" tab, which the page-level data-scope
          banner tags 'prod-live' because ITS OTHER sub-view (funnel
          dashboard) does redirect. A static caption here, not a second
          banner mechanism, disambiguates this specific sub-view. */}
      {/* DEC-4: /summary counts eval traffic, exactly like /by-user below and
          like /surface-spend on the "Kosten pro Feature" view (whose
          include_eval toggle defaults to true). Stated explicitly because the
          figures were once computed over DIFFERENT populations and nothing on
          screen said so. If eval spend must be excluded, that view's toggle is
          the way — there is no per-surface split here. */}
      <p style={{ color: COLORS.gray, fontSize: 11, marginTop: -6, marginBottom: 12 }}>
        Diese AI-Statistiken stammen ausschließlich aus der Datenbank dieser Umgebung — anders als
        der Funnel-Bereich dieses Tabs, gibt es hier keinen Production-Redirect. Alle Zahlen hier
        umfassen den <strong>gesamten</strong> Ion-Spend dieser Umgebung, also auch Eval-Runs und
        Hintergrund-Jobs — dieselbe Grundmenge wie „Kosten pro Feature“. Nach Feature aufgeschlüsselt
        (und auf Wunsch ohne Eval-Traffic) steht das dort.
      </p>

      <div className="analytics-kpi-grid" style={{ marginBottom: 16 }}>
        <KPICard
          icon={Icons.activity || '📊'}
          label="Tokens heute"
          value={formatNumber(summary.today.total_tokens)}
          sublabel={`${summary.today.request_count} Requests`}
          color="orange"
        />
        <KPICard
          icon={'💰'}
          label="Kosten heute (berechnet)"
          value={formatUSD(summary.today.cost_usd)}
          sublabel={`Ø ${formatUSD(parseFloat(summary.today.cost_usd || 0) / Math.max(1, summary.today.request_count))} / req`}
          color="orange"
        />
        <KPICard
          icon={Icons.chart || '📈'}
          label="Tokens MTD"
          value={formatNumber(summary.month_to_date.total_tokens)}
          sublabel={`${summary.month_to_date.request_count} Requests`}
          color="orange"
        />
        <KPICard
          icon={'💸'}
          label="Kosten MTD (berechnet)"
          value={formatUSD(summary.month_to_date.cost_usd)}
          color="orange"
        />
      </div>

      <h4 style={{ fontSize: 13, color: COLORS.gray, marginBottom: 6 }}>Cache-Effizienz (MTD)</h4>
      <div className="analytics-kpi-grid" style={{ marginBottom: 16 }}>
        <KPICard
          icon={'🎯'}
          label="Cache-Hit-Rate (Tokens)"
          value={`${summary.month_to_date.cache_hit_rate_pct}%`}
          sublabel="Anteil gecachter Input-Tokens"
          color={cacheRateColor(summary.month_to_date.cache_hit_rate_pct)}
        />
        <KPICard
          icon={'🔁'}
          label="Cache-Hit-Rate (Calls)"
          value={`${summary.month_to_date.call_cache_hit_rate_pct}%`}
          sublabel={`${formatNumber(summary.month_to_date.calls_with_cache_hit)} / ${formatNumber(summary.month_to_date.request_count)} Requests`}
          color={cacheRateColor(summary.month_to_date.call_cache_hit_rate_pct)}
        />
        <KPICard
          icon={'📥'}
          label="Cache Read vs Write (Tokens)"
          value={
            <CacheReadWriteBar
              readTokens={summary.month_to_date.cached_input_tokens}
              writeTokens={summary.month_to_date.cache_write_tokens}
            />
          }
          color="cyan"
        />
      </div>

      <div className="analytics-kpi-grid" style={{ marginBottom: 8 }}>
        <KPICard
          icon={'☁️'}
          label={<>Invocations{awsWindow}<AwsBadge /></>}
          value={cwValue(awsActuals?.cloudwatch?.bedrock_invocations)}
          sublabel={
            awsProblem.cloudwatch
              ? <AwsTileWarning message={awsProblem.cloudwatch} />
              : 'CloudWatch — ~1-2 min Verzögerung'
          }
          color="cyan"
        />
        <KPICard
          icon={'☁️'}
          label={<>Tokens{awsWindow}<AwsBadge /></>}
          value={cwValue(awsActuals?.cloudwatch?.bedrock_total_tokens)}
          sublabel={
            awsProblem.cloudwatch
              ? <AwsTileWarning message={awsProblem.cloudwatch} />
              : 'CloudWatch Input + Output'
          }
          color="cyan"
        />
        <KPICard
          icon={'💵'}
          label={<>Spend{spendWindow}<AwsBadge /></>}
          value={
            awsProblem.costExplorer
              ? '—'
              : formatUSD(awsActuals?.cost_explorer?.total_cost_usd || '0')
          }
          sublabel={
            awsProblem.costExplorer
              ? <AwsTileWarning message={awsProblem.costExplorer} />
              : accountWideSpend
                ? 'Konto-weit (alle Umgebungen) — Cost Explorer ~24h Lag'
                : 'Cost Explorer — ~24h Lag'
          }
          color="cyan"
        />
        <KPICard
          icon={'🛡️'}
          label="Admins (MTD)"
          value={formatNumber(summary.admins.total_tokens)}
          sublabel={`${summary.admins.request_count} Requests · ${formatUSD(summary.admins.cost_usd)}`}
          color="orange"
        />
      </div>

      {divergenceWarning && (
        <div
          className="analytics-warning-banner"
          style={{
            marginTop: 12,
            padding: 10,
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid ' + COLORS.red,
            borderRadius: 4,
            color: COLORS.red,
            fontSize: 13,
          }}
        >
          ⚠️ Berechnete Kosten ({formatUSD(computedTotal)}) weichen um{' '}
          {(delta * 100).toFixed(1)}% von AWS-Billing ({formatUSD(awsTotal)}) ab
          (Zeitraum: {comparisonPeriod}). Der AWS-Wert ist konto-weit (inkl. Staging- und
          Dev-Spend). Preistabelle prüfen (<code>backend/app/api/v1/chat/pricing.py</code>).
        </div>
      )}

      {divergenceInformational && (
        <div
          className="analytics-info-note"
          style={{
            marginTop: 12,
            padding: 10,
            border: '1px solid ' + COLORS.grid,
            borderRadius: 4,
            color: COLORS.gray,
            fontSize: 12,
          }}
        >
          ℹ️ Berechnete Kosten dieser Umgebung ({formatUSD(computedTotal)}) und AWS-Spend
          ({formatUSD(awsTotal)}) liegen {(delta * 100).toFixed(1)}% auseinander
          (Zeitraum: {comparisonPeriod}). Das ist erwartet: Der AWS-Wert ist konto-weit
          (inkl. Staging- und Dev-Spend) — Cost Explorer rechnet das gesamte AWS-Konto ab
          (alle Umgebungen, Staging, Dev und Production), die berechneten Kosten zählen nur
          diese Umgebung.
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Time-series chart
// --------------------------------------------------------------------------

// `showCacheSeries` (AI statistics dashboard, 2026-07) — optional cache
// read/write stacked-area overlay, OFF by default per plan §5 (avoids
// cluttering the default token/cost view; opt-in via the toggle button in
// the parent view).
function UsageTimeseriesChart({ points, awsDaily, showCacheSeries }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const buckets = (points || []).map((p) => p.bucket.slice(0, 10));
    const totalTokens = (points || []).map((p) => p.input_tokens + p.output_tokens);
    const cost = (points || []).map((p) => parseFloat(p.cost_usd || '0'));
    const awsMap = new Map((awsDaily || []).map((d) => [d.date, parseFloat(d.cost_usd || '0')]));
    const awsCostSeries = buckets.map((b) => awsMap.get(b) ?? null);
    const cacheRead = (points || []).map((p) => p.cached_input_tokens ?? 0);
    const cacheWrite = (points || []).map((p) => p.cache_write_tokens ?? 0);

    const legendData = ['Tokens (berechnet)', 'Kosten (berechnet)', 'Kosten (AWS)'];
    if (showCacheSeries) legendData.push('Cache Read', 'Cache Write');

    const series = [
      {
        name: 'Tokens (berechnet)', type: 'bar', data: totalTokens,
        itemStyle: { color: COLORS.orange + 'cc' },
      },
      {
        name: 'Kosten (berechnet)', type: 'line', yAxisIndex: 1, data: cost,
        smooth: true, itemStyle: { color: COLORS.orange }, lineStyle: { width: 2 },
      },
      {
        name: 'Kosten (AWS)', type: 'line', yAxisIndex: 1, data: awsCostSeries,
        smooth: true, itemStyle: { color: COLORS.cyan },
        lineStyle: { width: 2, type: 'dashed' },
        connectNulls: false,
      },
    ];
    if (showCacheSeries) {
      series.push(
        {
          name: 'Cache Read', type: 'line', data: cacheRead,
          stack: 'cache', areaStyle: { opacity: 0.3 }, smooth: true,
          itemStyle: { color: COLORS.cyanLight }, lineStyle: { width: 1 }, symbol: 'none',
        },
        {
          name: 'Cache Write', type: 'line', data: cacheWrite,
          stack: 'cache', areaStyle: { opacity: 0.3 }, smooth: true,
          itemStyle: { color: COLORS.purple }, lineStyle: { width: 1 }, symbol: 'none',
        },
      );
    }

    chartInstance.current.setOption({
      ...chartTheme,
      grid: { left: 60, right: 70, top: 30, bottom: 40 },
      tooltip: { ...chartTheme.tooltip, trigger: 'axis' },
      legend: {
        ...chartTheme.legend,
        top: 0,
        right: 0,
        data: legendData,
      },
      xAxis: {
        type: 'category',
        data: buckets,
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 10, rotate: 45 },
      },
      yAxis: [
        {
          type: 'value', name: 'Tokens',
          axisLabel: { color: COLORS.gray, fontSize: 11, formatter: (v) => formatNumber(v) },
          splitLine: { lineStyle: { color: COLORS.grid } },
        },
        {
          type: 'value', name: 'Kosten USD',
          axisLabel: { color: COLORS.gray, fontSize: 11, formatter: (v) => '$' + v.toFixed(2) },
          splitLine: { show: false },
        },
      ],
      series,
    }, { notMerge: true });

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [points, awsDaily, showCacheSeries]);

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);

  if (!points || points.length === 0) {
    return <div className="empty-state">Noch keine AI-Nutzung im Zeitraum.</div>;
  }
  return <div ref={chartRef} style={{ width: '100%', height: 320 }} />;
}

// --------------------------------------------------------------------------
// Per-user table
// --------------------------------------------------------------------------

function profilePillColor(profile) {
  if (profile === 'pro') return COLORS.orange;
  if (profile === 'demo') return COLORS.cyan;
  if (profile === 'batemo') return '#c9a53b';
  return COLORS.gray;
}

// Page sizes offered by the pager. Capped well below the endpoint's limit=1000
// — a 1000-row spend table is not read, it is scrolled past.
const PAGE_SIZES = [25, 50, 100, 250];

// Why the Profile and Monthly-cycle columns carry no sort affordance: they are
// a LIVE per-user quota snapshot (resolve_limits at read time, not scoped to
// the selected range), so the server can only order the population by the
// range-scoped aggregate columns. Sorting them here would mean sorting one
// page — the exact bug this table was rebuilt to remove.
const UNSORTABLE_HINT =
  'Nicht sortierbar: Live-Snapshot des aktuellen Monatszyklus, nicht Teil der Zeitraum-Aggregation.';

function TablePager({ offset, pageSize, total, count, onOffset, onPageSize }) {
  const last = offset + count;
  // `count === 0` past the end (total shrank between page loads) must not read
  // "51–50 von 40" — say "0 von 40" and let Zurück get out of there.
  const rangeLabel = count === 0 ? `0 von ${total}` : `${offset + 1}–${last} von ${total}`;
  const canPrev = offset > 0;
  const canNext = last < total;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 8,
        fontSize: 12,
        color: COLORS.gray,
      }}
    >
      <span>{rangeLabel}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          Pro Seite
          <select
            value={pageSize}
            onChange={(e) => onPageSize(parseInt(e.target.value, 10))}
            className="analytics-period-select"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          disabled={!canPrev}
          onClick={() => onOffset(Math.max(0, offset - pageSize))}
        >
          ← Zurück
        </button>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          disabled={!canNext}
          onClick={() => onOffset(offset + pageSize)}
        >
          Weiter →
        </button>
      </div>
    </div>
  );
}

// Ion AI limits system (2026-07): `showTokens` toggles the Kosten/Monthly
// columns between USD (default, primary per §1.3) and the Insights Token
// projection (`*_tokens` fields, computed server-side via usd_to_tokens() —
// never recomputed client-side).
//
// Ordering and paging are delegated to the server (`sort_by`/`sort_order`/
// `offset` on /admin/ai-usage/by-user). This component NEVER reorders `users`:
// it holds one page of a larger population, so a local sort would rename the
// cheapest row on this page to "the cheapest user".
function UserTable({
  users,
  total,
  offset,
  pageSize,
  sortBy,
  sortOrder,
  onSort,
  onOffset,
  onPageSize,
  showTokens,
  loading,
}) {
  if (!users) return <div className="analytics-loading"><div className="spinner" /></div>;
  if (users.length === 0 && offset === 0) {
    return <div className="empty-state">Noch keine Nutzer-Aktivität.</div>;
  }

  const th = (key, label, align) => {
    const active = sortBy === key;
    return (
      <th
        onClick={() => onSort(key)}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: align }}
        title={
          active
            ? `Sortiert ${sortOrder === 'desc' ? 'absteigend' : 'aufsteigend'} — klicken zum Umkehren`
            : 'Serverseitig über alle Nutzer sortieren'
        }
      >
        {label}{' '}
        <span style={{ color: active ? COLORS.orange : COLORS.grid }}>
          {active ? (sortOrder === 'desc' ? '▼' : '▲') : '↕'}
        </span>
      </th>
    );
  };

  const thStatic = (label) => (
    <th title={UNSORTABLE_HINT} style={{ color: COLORS.gray }}>
      {label}
    </th>
  );

  return (
    <div className="analytics-table-wrapper" style={{ overflowX: 'auto' }}>
      <table
        className="analytics-table"
        style={{ width: '100%', fontSize: 13, opacity: loading ? 0.5 : 1 }}
      >
        <thead>
          <tr>
            {th('email', 'User', 'left')}
            {th('role', 'Rolle')}
            {thStatic('Profile')}
            {th('input_tokens', 'Input')}
            {th('output_tokens', 'Output')}
            {th('cached_input_tokens', 'Cache')}
            {th('total_tokens', 'Total')}
            {th('cost', showTokens ? 'Kosten (Tokens)' : 'Kosten')}
            {th('requests', 'Requests')}
            {th('conversations', 'Conversations')}
            {thStatic('Monthly cycle')}
            {th('last_activity', 'Letzte Aktivität')}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id}>
              <td style={{ textAlign: 'left' }}>
                {(u.first_name || u.last_name)
                  ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                  : u.email}
                <div style={{ color: COLORS.gray, fontSize: 11 }}>{u.email}</div>
              </td>
              <td>{u.admin_role ? <span style={{ color: COLORS.cyan }}>{u.admin_role}</span> : '—'}</td>
              <td>
                {u.tier_profile ? (
                  <span style={{ color: profilePillColor(u.tier_profile), fontWeight: 600, fontSize: 11 }}>
                    {u.tier_profile.toUpperCase()}
                  </span>
                ) : (
                  <span style={{ color: COLORS.gray }}>—</span>
                )}
                {u.monthly_window_error && <span title="Monthly window unresolvable (data issue)"> ⚠</span>}
              </td>
              <td style={{ textAlign: 'right' }}>{formatNumber(u.input_tokens)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(u.output_tokens)}</td>
              <td style={{ textAlign: 'right', color: COLORS.gray }}>{formatNumber(u.cached_input_tokens)}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatNumber(u.total_tokens)}</td>
              <td style={{ textAlign: 'right' }}>
                {showTokens ? `${u.cost_tokens ?? '—'} tok` : formatUSD(u.cost_usd)}
              </td>
              <td style={{ textAlign: 'right' }}>{u.request_count}</td>
              <td style={{ textAlign: 'right' }}>{u.conversation_count}</td>
              <td style={{ textAlign: 'right' }}>
                {u.tier_profile ? (
                  <>
                    {u.monthly_used_pct}%
                    <div style={{ color: COLORS.gray, fontSize: 11 }}>
                      {showTokens
                        ? `${u.monthly_used_tokens ?? '—'} / ${u.monthly_limit_tokens ?? '∞'}`
                        : `${formatUSD(u.monthly_used_usd)} / ${
                            u.monthly_limit_usd === 'unlimited' ? '∞' : formatUSD(u.monthly_limit_usd)
                          }`}
                    </div>
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td style={{ textAlign: 'right', color: COLORS.gray }}>{fmtRelTime(u.last_activity_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <TablePager
        offset={offset}
        pageSize={pageSize}
        total={total}
        count={users.length}
        onOffset={onOffset}
        onPageSize={onPageSize}
      />
    </div>
  );
}

// --------------------------------------------------------------------------
// Limit Utilization — per-tier/profile rollup (Ion AI limits system, 2026-07)
// --------------------------------------------------------------------------

function LimitUtilizationSection({ data, loading, showTokens }) {
  if (loading) return <div className="analytics-loading"><div className="spinner" /></div>;
  if (!data || !data.groups || data.groups.length === 0) {
    return <div className="empty-state">Keine Profile mit aktiven Limits.</div>;
  }

  return (
    <div className="analytics-table-wrapper" style={{ overflowX: 'auto' }}>
      <table className="analytics-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Profile</th>
            <th>Users</th>
            <th>Ø Monthly used</th>
            <th>Median Monthly used</th>
            <th>≥80% on any limit</th>
            <th>Blocked (≥100% on any)</th>
            <th>Outstanding top-ups</th>
          </tr>
        </thead>
        <tbody>
          {data.groups.map((g) => (
            <tr key={g.tier_profile}>
              <td style={{ textAlign: 'left' }}>
                <span style={{ color: profilePillColor(g.tier_profile), fontWeight: 700 }}>
                  {g.tier_profile.toUpperCase()}
                </span>
              </td>
              <td style={{ textAlign: 'right' }}>{g.user_count}</td>
              <td style={{ textAlign: 'right' }}>{g.avg_monthly_used_pct}%</td>
              <td style={{ textAlign: 'right' }}>{g.median_monthly_used_pct}%</td>
              <td style={{ textAlign: 'right', color: g.count_warning_any > 0 ? COLORS.orange : undefined }}>
                {g.count_warning_any}
              </td>
              <td style={{ textAlign: 'right', color: g.count_blocked_any > 0 ? COLORS.red : undefined }}>
                {g.count_blocked_any}
              </td>
              <td style={{ textAlign: 'right' }}>
                {showTokens ? `${g.outstanding_topup_tokens} tok` : formatUSD(g.outstanding_topup_usd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ color: COLORS.gray, fontSize: 11, marginTop: 8 }}>
        Live snapshot (generated {fmtRelTime(data.generated_at)}) — not scoped to the selected
        time range above; this reflects where each profile stands on its CURRENT monthly cycle.
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------
// Operational: latency, stop reasons, model breakdown, heatmap
// --------------------------------------------------------------------------

function LatencyChart({ rows }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !rows?.length) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }
    const models = rows.map((r) => r.model_id.replace(/^(eu|global|us)\./, ''));
    chartInstance.current.setOption({
      ...chartTheme,
      grid: { left: 60, right: 30, top: 30, bottom: 90 },
      tooltip: { ...chartTheme.tooltip, trigger: 'axis' },
      legend: { ...chartTheme.legend, top: 0, right: 0, data: ['p50', 'p95', 'p99'] },
      xAxis: {
        type: 'category', data: models,
        axisLabel: { color: COLORS.gray, fontSize: 10, rotate: 25, interval: 0 },
      },
      yAxis: {
        type: 'value', name: 'ms',
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [
        { name: 'p50', type: 'bar', data: rows.map((r) => r.p50_ms), itemStyle: { color: COLORS.orange } },
        { name: 'p95', type: 'bar', data: rows.map((r) => r.p95_ms), itemStyle: { color: COLORS.orangeLight } },
        { name: 'p99', type: 'bar', data: rows.map((r) => r.p99_ms), itemStyle: { color: COLORS.red } },
      ],
    });
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rows]);

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);

  if (!rows || rows.length === 0) return <div className="empty-state">Keine Latenz-Daten.</div>;
  return <div ref={chartRef} style={{ width: '100%', height: 260 }} />;
}

function StopReasonChart({ rows }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  useEffect(() => {
    if (!chartRef.current || !rows?.length) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }
    chartInstance.current.setOption({
      ...chartTheme,
      tooltip: { ...chartTheme.tooltip, trigger: 'item' },
      legend: { ...chartTheme.legend, orient: 'vertical', left: 10, top: 'middle' },
      series: [{
        name: 'Stop Reasons', type: 'pie', radius: ['40%', '70%'], center: ['65%', '50%'],
        data: rows.map((r) => ({ value: r.count, name: r.stop_reason })),
        emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.5)' } },
        label: { color: COLORS.gray },
      }],
      color: [COLORS.green, COLORS.orange, COLORS.red, COLORS.cyan, COLORS.purple, COLORS.gray],
    });
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rows]);
  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);
  if (!rows || rows.length === 0) return <div className="empty-state">Keine Daten.</div>;
  return <div ref={chartRef} style={{ width: '100%', height: 260 }} />;
}

function ModelBreakdown({ rows }) {
  if (!rows || rows.length === 0) return <div className="empty-state">Noch keine Modell-Nutzung.</div>;
  return (
    <div className="analytics-table-wrapper" style={{ overflowX: 'auto' }}>
      <table className="analytics-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Model</th>
            <th>Requests</th>
            <th>Tokens</th>
            <th>Ø Tokens / req</th>
            <th>Input</th>
            <th>Output</th>
            <th>Cached</th>
            <th>Write</th>
            <th>Kosten</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.model_id}>
              <td style={{ textAlign: 'left' }}>{r.model_id.replace(/^(eu|global|us)\./, '')}</td>
              <td style={{ textAlign: 'right' }}>{r.request_count}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.total_tokens)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.avg_tokens_per_request)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.input_tokens ?? 0)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.output_tokens ?? 0)}</td>
              <td style={{ textAlign: 'right', color: COLORS.gray }}>{formatNumber(r.cached_input_tokens ?? 0)}</td>
              <td style={{ textAlign: 'right', color: COLORS.gray }}>{formatNumber(r.cache_write_tokens ?? 0)}</td>
              <td style={{ textAlign: 'right' }}>{formatUSD(r.cost_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VolumeHeatmap({ rows }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }
    const data = (rows || []).map((r) => [r.hour, r.dow, r.count]);
    const maxCount = Math.max(1, ...data.map((d) => d[2]));
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const hours = Array.from({ length: 24 }, (_, i) => `${i}`);
    chartInstance.current.setOption({
      ...chartTheme,
      grid: { top: 30, right: 20, bottom: 30, left: 40 },
      tooltip: {
        ...chartTheme.tooltip,
        formatter: (p) => `${days[p.data[1]]} ${p.data[0]}:00 — ${p.data[2]} Requests`,
      },
      xAxis: { type: 'category', data: hours, axisLabel: { color: COLORS.gray, fontSize: 10 } },
      yAxis: { type: 'category', data: days, axisLabel: { color: COLORS.gray, fontSize: 11 } },
      visualMap: {
        min: 0, max: maxCount, calculable: false, orient: 'horizontal', left: 'center', bottom: 0,
        textStyle: { color: COLORS.gray, fontSize: 10 },
        inRange: { color: ['rgba(234,91,12,0.05)', COLORS.orange] },
      },
      series: [{
        name: 'Requests', type: 'heatmap', data,
        label: { show: false },
      }],
    });
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rows]);
  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);
  if (!rows || rows.length === 0) return <div className="empty-state">Noch keine Daten für die Heatmap.</div>;
  return <div ref={chartRef} style={{ width: '100%', height: 220 }} />;
}

// --------------------------------------------------------------------------
// Main view
// --------------------------------------------------------------------------

export function AnalyticsAiUsageView({ periodDays = 30 }) {
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [byUser, setByUser] = useState(null);
  const [operational, setOperational] = useState(null);
  const [awsActuals, setAwsActuals] = useState(null);
  // Ion AI limits system (2026-07) — per-tier/profile limit-utilization
  // rollup. Live snapshot, independent of periodDays (see the section note).
  const [limitUtilization, setLimitUtilization] = useState(null);
  const [limitUtilizationLoading, setLimitUtilizationLoading] = useState(true);
  // USD/token display toggle (§1.3 — dollars primary, tokens an additive
  // nice-to-have on this admin dashboard). Shared by UserTable + the
  // limit-utilization table so the two never show different units at once.
  const [showTokens, setShowTokens] = useState(false);
  // Cache read/write timeseries overlay (§5) — off by default to avoid
  // cluttering the default token/cost chart.
  const [showCacheSeries, setShowCacheSeries] = useState(false);
  // Per-user table query — sorting AND paging are server-side, so all four
  // fields are request parameters, never post-processing of a fetched page.
  const [sortBy, setSortBy] = useState('cost');
  const [sortOrder, setSortOrder] = useState('desc');
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [byUserLoading, setByUserLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Guards against an out-of-order page response overwriting a newer one when
  // an admin clicks through pages / headers faster than the API answers.
  const byUserReq = useRef(0);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    const base = getApiBase();
    const bucket = periodDays <= 2 ? 'hour' : 'day';

    try {
      const [sRes, tRes, oRes, aRes] = await Promise.all([
        authenticatedFetch(`${base}/admin/ai-usage/summary`),
        authenticatedFetch(`${base}/admin/ai-usage/timeseries?bucket=${bucket}&range_days=${periodDays}`),
        authenticatedFetch(`${base}/admin/ai-usage/operational?range_days=${periodDays}&bucket=${bucket}`),
        authenticatedFetch(`${base}/admin/ai-usage/aws-actuals?range_days=${Math.min(periodDays, 90)}`).catch(() => null),
      ]);

      if (sRes.ok) setSummary(await sRes.json());
      if (tRes.ok) setTimeseries(await tRes.json());
      if (oRes.ok) setOperational(await oRes.json());
      if (aRes?.ok) setAwsActuals(await aRes.json());
    } catch (err) {
      console.error('AI usage fetch error:', err);
      setError('Verbindungsfehler beim Laden der AI-Nutzungsdaten.');
    } finally {
      setLoading(false);
    }
  };

  // Separate from fetchAll: paging/sorting the user table must not refetch the
  // summary, the chart and the AWS block (which don't depend on either).
  const fetchByUser = async () => {
    const reqId = ++byUserReq.current;
    setByUserLoading(true);
    try {
      const data = await getAiUsageByUser({
        rangeDays: periodDays,
        limit: pageSize,
        offset,
        sortBy,
        sortOrder,
      });
      if (reqId === byUserReq.current) setByUser(data);
    } catch (err) {
      console.error('AI usage by-user fetch error:', err);
    } finally {
      if (reqId === byUserReq.current) setByUserLoading(false);
    }
  };

  // Clicking a header sorts server-side over the whole population and returns
  // to page 1 — staying on page 5 of a re-sorted list shows an arbitrary slice.
  const handleSort = (key) => {
    if (key === sortBy) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(key);
      // Text columns read naturally A→Z; numbers and dates read "worst first".
      setSortOrder(key === 'email' || key === 'role' ? 'asc' : 'desc');
    }
    setOffset(0);
  };

  const fetchLimitUtilization = async () => {
    setLimitUtilizationLoading(true);
    try {
      const res = await authenticatedFetch(`${getApiBase()}/admin/ai-usage/limit-utilization`);
      if (res.ok) setLimitUtilization(await res.json());
    } catch (err) {
      console.error('Limit-utilization fetch error:', err);
    } finally {
      setLimitUtilizationLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [periodDays]);
  useEffect(() => { fetchByUser(); /* eslint-disable-line */ }, [periodDays, sortBy, sortOrder, offset, pageSize]);
  // A new period is a new population — page 5 of the old one means nothing.
  // When the offset was non-zero this costs one extra (server-cached) fetch
  // before the reset lands; the byUserReq guard above is what keeps the
  // discarded response from painting the old page.
  useEffect(() => { setOffset(0); }, [periodDays]);
  // Independent of periodDays/sort — this is a live snapshot, not a
  // range query (see LimitUtilizationSection's footnote).
  useEffect(() => { fetchLimitUtilization(); /* eslint-disable-line */ }, []);

  if (error) return <div className="analytics-error"><p>{error}</p><button onClick={fetchAll}>Erneut versuchen</button></div>;

  return (
    <div className="ai-usage-view" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={() => setShowTokens((v) => !v)}
          title="Toggle Kosten-Anzeige zwischen USD und Insights Tokens ($0.25/Token)"
        >
          {showTokens ? 'Show USD' : 'Show Insights Tokens'}
        </button>
      </div>

      <SummarySection summary={summary} awsActuals={awsActuals} loading={loading && !summary} />

      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 className="section-title">Nutzung im Zeitverlauf</h3>
          <label style={{ fontSize: 12, color: COLORS.gray, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={showCacheSeries}
              onChange={(e) => setShowCacheSeries(e.target.checked)}
            />
            Cache Read/Write anzeigen
          </label>
        </div>
        {timeseries
          ? (
            <UsageTimeseriesChart
              points={timeseries.points}
              awsDaily={awsActuals?.cost_explorer?.daily}
              showCacheSeries={showCacheSeries}
            />
          )
          : <div className="analytics-loading"><div className="spinner" /></div>}
      </section>

      <section>
        <h3 className="section-title">Limit Utilization</h3>
        <LimitUtilizationSection
          data={limitUtilization}
          loading={limitUtilizationLoading && !limitUtilization}
          showTokens={showTokens}
        />
      </section>

      <section>
        <h3 className="section-title">Nutzer</h3>
        <UserTable
          users={byUser?.users}
          total={byUser?.total ?? 0}
          offset={byUser?.offset ?? offset}
          pageSize={pageSize}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onOffset={setOffset}
          onPageSize={(n) => { setPageSize(n); setOffset(0); }}
          showTokens={showTokens}
          loading={byUserLoading}
        />
      </section>

      <section>
        <h3 className="section-title">Operational — Latenz, Error-Rate, Modelle</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <h4 style={{ fontSize: 13, color: COLORS.gray, marginBottom: 6 }}>Latenz-Perzentile pro Modell</h4>
            <LatencyChart rows={operational?.latency_percentiles} />
          </div>
          <div>
            <h4 style={{ fontSize: 13, color: COLORS.gray, marginBottom: 6 }}>Stop-Reason-Verteilung</h4>
            <StopReasonChart rows={operational?.stop_reasons} />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 13, color: COLORS.gray, marginBottom: 6 }}>Model Breakdown</h4>
          <ModelBreakdown rows={operational?.model_breakdown} />
        </div>
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 13, color: COLORS.gray, marginBottom: 6 }}>Volume Heatmap (UTC)</h4>
          <VolumeHeatmap rows={operational?.volume_heatmap} />
        </div>
      </section>
    </div>
  );
}
